import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { redirect, useLoaderData } from '@remix-run/react'
import { useHydrateAtoms } from 'jotai/utils'
import { omit } from 'lodash-es'
import { aListingContact } from '~/atoms'
import {
  getOrganizationSlugFilter,
  ListingTransaction,
  TokenUser,
  UserGroup,
  UserRole,
} from '~/models'
import {
  activityHelper,
  findListing,
  findListingContact,
  findListings,
  findOrganizationFromRequest,
} from '~/mongo.server'
import { getRoutMeta, transformImageUrl } from '~/utils'
import { parseRequestUser } from '~/utils.server'
import ListingDetailLayout from './Layout'

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const slug = params.slug

  if (!slug) {
    throw new Response('Listing Not found', { status: 404 })
  }
  const organization = await findOrganizationFromRequest(request)
  if (!organization) {
    throw new Response('Listing Not found', { status: 404 })
  }

  const user = await parseRequestUser(request)
  const orgFindCondition = {
    $or: [
      {
        'organization.slug': !user
          ? organization.slug
          : getOrganizationSlugFilter(organization),
      },
      { externalMembershipSlugs: { $in: [organization.slug] } },
    ],
  }
  const [listing, listingContact] = await Promise.all([
    findListing({
      slug,
      ...orgFindCondition,
    }),
    findListingContact(slug, organization.slug, request),
  ])

  if (!listing && slug.length >= 8) {
    const tid = slug.slice(0, 8).toLowerCase()
    const updatedListing = await findListing({
      tid,
      ...orgFindCondition,
    })
    if (updatedListing) {
      return redirect(`/listings/${updatedListing.slug}`, { status: 302 })
    }
  }
  if (!listing) {
    try {
      const updateListing = await findListings({
        ignoreOrgCheck: true,
        find: {
          $and: [
            { ...orgFindCondition },
            {
              $or: [
                { bacId: slug },
                { publicBacId: slug },
                { 'leaseSpaces.bacId': slug },
                { 'leaseSpaces.publicBacId': slug },
              ],
            },
          ],
        },
      })

      if (updateListing.listings.length > 0) {
        return redirect(`/listings/${updateListing.listings[0].slug}`, {
          status: 302,
        })
      }
    } catch (error) {
      console.error('Error finding listing by BAC ID:', error)
    }
  }
  if (!listing) {
    throw new Response('Listing Not found', { status: 404 })
  }
  await activityHelper(request)
    .source('app')
    .type('view')
    .related('listing', listing)
    .write()

  const getCanEdit = (user: TokenUser | null) => {
    if (!user) {
      return false
    }
    const case1 = listing.brokers.some(
      (broker) => broker._id.toString() === user._id.toString()
    )
    const case2 =
      (user.group === UserGroup.companyAdmin ||
        user.role === UserRole.coordinator) &&
      listing.company._id.toString() === user.company?._id.toString()
    return (
      user.group === UserGroup.superAdmin ||
      user.group === UserGroup.organizationAdmin ||
      case1 ||
      case2
    )
  }

  const canEdit =
    getCanEdit(user) && organization.slug === listing.organization.slug

  if (!canEdit && listing.status === 'draft') {
    throw new Response('Listing Not found', { status: 404 })
  }

  if (!user) {
    listing.meta = omit(listing.meta, ['notes'])
    delete listing.transactions // could move to projection
  } else if (!canEdit && listing.transactions) {
    // remove confidential lease information. Keep footage, acres, date, brokers
    listing.transactions = listing.transactions!.map((transaction) => {
      if (transaction.confidential === false) {
        // If not confidential, return rate and price for member display
        return omit(transaction, [
          'lease.totalLeaseValue',
          'lease.rateType',
          'lease.structure',
          'lease.tenantName',
          'lease.startDate',
          'lease.termInMonths',
          'sale.terms',
        ]) as ListingTransaction
      }
      return omit(transaction, [
        'lease.rate',
        'lease.totalLeaseValue',
        'lease.rateType',
        'lease.structure',
        'lease.tenantName',
        'lease.startDate',
        'lease.termInMonths',
        'sale.price',
        'sale.terms',
      ]) as ListingTransaction
    })
  }

  // Remove sensitive information
  delete listing.expirationDate

  return {
    listing,
    canEdit,
    listingContact,
  }
}

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const title = data?.listing.name || data?.listing.property.address || ''
  return getRoutMeta({
    matches,
    title,
    description: data?.listing.meta.description,
    image:
      (data?.listing.images || []).length > 0
        ? transformImageUrl(data!.listing.images![0].thumbUrl)
        : undefined,
  })
}

export default function AppListingDetailRoute() {
  const { listing, canEdit, listingContact } = useLoaderData<typeof loader>()
  useHydrateAtoms([[aListingContact, listingContact]])

  return <ListingDetailLayout canEdit={canEdit} listing={listing} />
}
